/**
 * Quick View Modal Logic
 * Handles global quick view interactions
 */

document.addEventListener('DOMContentLoaded', () => {

    // 1. Singleton initialization & Cache
    if (window.QuickViewInitialized) return;
    window.QuickViewInitialized = true;

    // Global Cache for Eager Fetching
    const productCache = new Map();

    const modal = document.getElementById('QuickViewModal');
    if (!modal) return;

    const content = modal.querySelector('.js-qv-content');
    const closeBtn = modal.querySelector('.js-qv-close');
    const overlay = modal;

    // 2. Event Delegation for "Eye" buttons & Pre-fetching

    // A. Pre-fetch on Hover (Eager Loading)
    document.addEventListener('mouseover', (e) => {
        // Looking for the trigger button OR the card wrapper to start fetching early
        const trigger = e.target.closest('.js-quick-view-trigger');
        const wrapper = e.target.closest('.card-wrapper');

        // Prefer handle from trigger, then calculate if needed from wrapper
        let handle = trigger ? trigger.dataset.handle :
            (wrapper ? wrapper.querySelector('.js-quick-view-trigger')?.dataset.handle : null);

        if (handle && !productCache.has(handle)) {
            // Start fetching immediately, store promise
            if (typeof Shopify !== 'undefined' && Shopify.designMode) { console.log(`[QV] Pre-fetching: ${handle}`); }
            const fetchPromise = fetch(`/products/${handle}.js`)
                .then(res => {
                    if (!res.ok) return null;
                    return res.json();
                })
                .catch(err => {
                    console.error('[QV] Pre-fetch error', err);
                    return null;
                });

            productCache.set(handle, fetchPromise);
        }
    }, { passive: true }); // Passive for better scroll performance

    // B. Click Handler
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('.js-quick-view-trigger');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();

        const handle = btn.dataset.handle;
        if (handle) {
            // HERO EXTRACTION: Get data from the card immediately
            const card = btn.closest('.card-wrapper') || btn.closest('.grid__item') || btn.closest('.product-card') || btn.closest('.media')?.parentElement;

            // Try to find image more robustly
            let img = card ? (card.querySelector('img.motion-reduce') || card.querySelector('img')) : null;

            const data = {
                handle: handle,
                imageSrc: img ? img.src : null,
                title: card ? card.querySelector('.card__heading')?.innerText : null,
                price: card ? card.querySelector('.price-item--regular')?.innerText : null
            };
            openModal(data);
        }
    });

    // 3. Modal Controls
    async function openModal(data) {
        modal.classList.add('is-visible');
        document.body.style.overflow = 'hidden';

        // OPTIMISTIC RENDER: Show what we know immediately
        // Build a Skeleton + Real Data content
        const optimisticHTML = `
        <div class="qv-grid">
           <div class="qv-image-column">
              <div class="qv-gallery-slider">
                 <div class="qv-gallery-frame">
                    ${data.imageSrc ? `<img src="${data.imageSrc}" class="qv-image-img active" style="object-fit:contain; opacity:0.8;">` : ''}
                 </div>
              </div>
           </div>
           <div class="qv-details-column">
              <h2 class="qv-product-title">${data.title || 'Loading...'}</h2>
              <div class="qv-product-price">${data.price || ''}</div>
              
              <!-- Skeleton for description/variants -->
              <div style="background:#f4f4f4; height:12px; margin-bottom:10px; border-radius:4px; width:100%;"></div>
              <div style="background:#f4f4f4; height:12px; margin-bottom:30px; border-radius:4px; width:80%;"></div>
              
              <div style="background:#f4f4f4; height:40px; margin-bottom:10px; border-radius:4px; width:100%;"></div>
              <div style="background:#f4f4f4; height:40px; margin-bottom:10px; border-radius:4px; width:100%;"></div>
           </div>
        </div>
        `;

        // Only inject optimistic if cache is empty to avoid flicker with faster cache
        if (!productCache.has(data.handle)) {
            content.innerHTML = optimisticHTML;
        }

        // Check Cache
        let productData = null;

        // Wait for cache (even if it's already fetching from hover) or start new fetch
        if (productCache.has(data.handle)) {
            try {
                productData = await productCache.get(data.handle);
            } catch (err) { console.error(err); }
        } else {
            try {
                const res = await fetch(`/products/${data.handle}.js`);
                if (!res.ok) {
                    productData = null;
                } else {
                    productData = await res.json();
                    productCache.set(data.handle, Promise.resolve(productData));
                }
            } catch (err) { console.error('[QV] Fetch error', err); productData = null; }
        }

        if (productData) {
            renderProduct(productData);
        } else {
            content.innerHTML = `
                <div style="padding: 60px 40px; text-align: center;">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom: 16px; opacity: 0.5;">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <p style="margin: 0 0 16px 0; font-size: 16px; color: #666;">Unable to load product details</p>
                    <button class="qv-retry-btn" style="padding: 12px 24px; background: #000; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;">
                        Try Again
                    </button>
                </div>
            `;
            // Add retry functionality
            const retryBtn = content.querySelector('.qv-retry-btn');
            if (retryBtn) {
                retryBtn.addEventListener('click', () => {
                    productCache.delete(data.handle);
                    openModal(data);
                });
            }
        }
    }

    function closeModal() {
        modal.classList.remove('is-visible');
        document.body.style.overflow = '';
    }

    closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeModal();
    });

    document.addEventListener('keydown', (e) => {
        if (!modal.classList.contains('is-visible')) return;

        if (e.key === 'Escape') {
            closeModal();
        }

        // Trap Focus
        if (e.key === 'Tab') {
            const focusable = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    last.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === last) {
                    first.focus();
                    e.preventDefault();
                }
            }
        }
    });

    // rendering logic...

    // 5. Rendering
    function renderProduct(product) {
        // Currency Formatter - uses store currency from Shopify settings
        const formatMoney = (cents) => {
            const currency = window.Shopify?.currency?.active || 'USD';
            const locale = document.documentElement.lang || 'en-US';
            return (cents / 100).toLocaleString(locale, { style: 'currency', currency: currency });
        };

        // Images fallback + Hand Towel reordering
        const rawMedia = product.media && product.media.length ? product.media : product.images; // API variance

        // For "Hand Towel" products, mirror card/PDP behavior:
        // treat the SECOND image as the primary one (first in the gallery),
        // and the original first image becomes secondary, followed by the rest.
        let media = rawMedia || [];
        const titleLower = (product.title || '').toLowerCase();
        if (titleLower.includes('hand towel') && media.length > 1) {
          const reordered = [];
          // second image first
          reordered.push(media[1]);
          // first image second
          reordered.push(media[0]);
          // then the rest in original order
          for (let i = 2; i < media.length; i++) {
            reordered.push(media[i]);
          }
          media = reordered;
        }

        const imagesHtml = media.map((img, index) => {
          const src = typeof img === 'string' ? img : img.src;
          const hiddenClass = index === 0 ? 'active' : '';
          return `<img src="${src}" class="qv-image-img ${hiddenClass}" data-index="${index}">`;
        }).join('');

        // Variants Logic
        let variantSectionHtml = '';
        let initialVariant = product.variants[0];

        if (product.variants.length > 1 && product.options) {
            variantSectionHtml = product.options.map((opt, optIndex) => {
                const name = typeof opt === 'string' ? opt : opt.name;
                const values = opt.values || []; // API

                return `
             <div class="qv-option-group">
               <label class="qv-option-label">${name}</label>
               <div class="qv-option-values">
                 ${values.map(val => {
                    const isSelected = initialVariant.options[optIndex] === val;
                    const isAvailableGlobally = product.variants.some(v => v.options[optIndex] === val && v.available);

                    return `<button type="button" class="qv-option-value ${isSelected ? 'selected' : ''} ${!isAvailableGlobally ? 'sold-out' : ''}" data-index="${optIndex}" data-value="${val}">${val}</button>`;
                }).join('')}
               </div>
             </div>
           `;
            }).join('');
        } else {
            // Single variant, implied
            variantSectionHtml = `<input type="hidden" name="id" value="${initialVariant.id}">`;
        }

        // HTML Template
        const template = `
        <div class="qv-grid">
           <!-- Gallery -->
           <div class="qv-image-column">
              <div class="qv-gallery-slider">
                 <div class="qv-gallery-frame">
                    ${imagesHtml}
                 </div>
                 
                 ${media.length > 1 ? `
                   <button type="button" class="qv-slider-nav qv-prev">&lsaquo;</button>
                   <button type="button" class="qv-slider-nav qv-next">&rsaquo;</button>
                 ` : ''}
              </div>
           </div>
  
           <!-- Info -->
           <div class="qv-details-column">
              <h2 class="qv-product-title">${product.title}</h2>
              <div class="qv-product-price">${formatMoney(product.price)}</div>
              
              <div class="qv-product-desc">
                ${product.description.replace(/<[^>]*>?/gm, '').substring(0, 160)}...
              </div>
  
              <form class="qv-add-form">
                 <input type="hidden" name="id" value="${initialVariant.id}">
                 <div class="js-variant-selectors">${variantSectionHtml}</div>
                 
                 <div class="qv-actions">
                    <button type="submit" class="qv-add-btn" ${initialVariant.available ? '' : 'disabled'}>
                       ${initialVariant.available ? 'Add to Cart' : 'Sold Out'}
                    </button>
                    <a href="${product.url}" class="qv-full-link">View Full Details</a>
                 </div>
              </form>
           </div>
        </div>
      `;

        content.innerHTML = template;

        // 6. Post-Render Bindings

        // Gallery Navigation Logic (Dynamic)
        const updateGalleryNav = () => {
            const visibleSlides = Array.from(content.querySelectorAll('.qv-image-img:not(.force-hide)'));
            if (visibleSlides.length === 0) return;

            // Ensure active slide is valid
            let activeIndex = visibleSlides.findIndex(s => s.classList.contains('active'));
            if (activeIndex === -1) {
                visibleSlides[0].classList.add('active');
                activeIndex = 0;
            }

            // Hide arrows if only 1 image
            const prevBtn = content.querySelector('.qv-prev');
            const nextBtn = content.querySelector('.qv-next');

            if (visibleSlides.length > 1) {
                if (prevBtn) prevBtn.style.display = 'flex';
                if (nextBtn) nextBtn.style.display = 'flex';
            } else {
                if (prevBtn) prevBtn.style.display = 'none';
                if (nextBtn) nextBtn.style.display = 'none';
            }
        };

        const gallery = content.querySelector('.qv-gallery-slider');

        // Navigation Wrappers
        if (gallery.querySelector('.qv-prev')) {
            gallery.querySelector('.qv-prev').addEventListener('click', () => {
                const visibleSlides = Array.from(content.querySelectorAll('.qv-image-img:not(.force-hide)'));
                if (visibleSlides.length <= 1) return;

                let currentIdx = visibleSlides.findIndex(s => s.classList.contains('active'));
                visibleSlides[currentIdx].classList.remove('active');

                let newIdx = currentIdx - 1;
                if (newIdx < 0) newIdx = visibleSlides.length - 1;

                visibleSlides[newIdx].classList.add('active');
            });

            gallery.querySelector('.qv-next').addEventListener('click', () => {
                const visibleSlides = Array.from(content.querySelectorAll('.qv-image-img:not(.force-hide)'));
                if (visibleSlides.length <= 1) return;

                let currentIdx = visibleSlides.findIndex(s => s.classList.contains('active'));
                visibleSlides[currentIdx].classList.remove('active');

                let newIdx = currentIdx + 1;
                if (newIdx >= visibleSlides.length) newIdx = 0;

                visibleSlides[newIdx].classList.add('active');
            });
        }

        // Image Filter Logic
        const updateGalleryFilters = (selectedOptions) => {
            // 1. Find Color Option Index
            const colorOptionIndex = product.options.findIndex(opt => {
                const name = (typeof opt === 'string' ? opt : opt.name).toLowerCase();
                return name.includes('color') || name.includes('colour');
            });

            if (colorOptionIndex === -1) return; // No color option

            const selectedColor = selectedOptions[colorOptionIndex].toLowerCase().trim();
            const allImages = content.querySelectorAll('.qv-image-img');

            // Get all possible color values to strict match
            const allColorValues = product.options[colorOptionIndex].values.map(v => v.toLowerCase().trim());

            // Re-implementing color matching logic
            allImages.forEach(img => {
                const mediaIndex = parseInt(img.dataset.index);
                const mediaItem = (product.media.length ? product.media : product.images)[mediaIndex];
                const altText = (mediaItem.alt || "").toLowerCase().trim();

                let imageBelongsToColor = null;

                for (const color of allColorValues) {
                    if (altText.includes(color)) {
                        imageBelongsToColor = color;
                        break;
                    }
                }

                if (imageBelongsToColor === null) {
                    img.classList.remove('force-hide'); // Generic
                } else if (imageBelongsToColor === selectedColor) {
                    img.classList.remove('force-hide'); // Match
                } else {
                    img.classList.add('force-hide'); // Mismatch
                    img.classList.remove('active'); // Hide active status if hiding
                }
            });

            updateGalleryNav();
        };

        // Smart Variant & Add to Cart Logic
        const form = content.querySelector('.qv-add-form');
        const addBtn = form.querySelector('.qv-add-btn');
        const idInput = form.querySelector('input[name="id"]');
        const priceEl = content.querySelector('.qv-product-price');

        if (product.variants.length > 1) {
            const optionBtns = content.querySelectorAll('.qv-option-value');
            optionBtns.forEach(btn => {
                btn.addEventListener('click', () => {
                    // Toggle visual state
                    const siblings = btn.parentElement.querySelectorAll('.qv-option-value');
                    siblings.forEach(s => s.classList.remove('selected'));
                    btn.classList.add('selected');

                    // 1. Get current complete selection
                    const currentOptions = Array.from(content.querySelectorAll('.qv-option-values')).map(
                        grp => grp.querySelector('.selected').dataset.value
                    );

                    // 2. Find specific matching variant
                    const matched = product.variants.find(v => {
                        return v.options.every((optVal, idx) => optVal === currentOptions[idx]);
                    });

                    // 3. Update UI based on match
                    if (matched) {
                        idInput.value = matched.id;
                        priceEl.textContent = formatMoney(matched.price);

                        if (matched.available) {
                            addBtn.textContent = 'Add to Cart';
                            addBtn.disabled = false;
                        } else {
                            addBtn.textContent = 'Sold Out';
                            addBtn.disabled = true;
                        }
                    } else {
                        addBtn.textContent = 'Unavailable';
                        addBtn.disabled = true;
                    }

                    // 4. Update availability of OTHER options based on this selection (simplified for brevity)
                    content.querySelectorAll('.qv-option-group').forEach((grp, grpIndex) => {
                        const buttons = grp.querySelectorAll('.qv-option-value');
                        buttons.forEach(optBtn => {
                            const value = optBtn.dataset.value;
                            const potentialMatch = product.variants.find(v => {
                                return v.options.every((vOpt, vIdx) => {
                                    if (vIdx === grpIndex) return vOpt === value;
                                    return vOpt === currentOptions[vIdx];
                                });
                            });

                            if (!potentialMatch || !potentialMatch.available) {
                                optBtn.classList.add('sold-out');
                            } else {
                                optBtn.classList.remove('sold-out');
                            }
                        });
                    });

                    // 5. Update Gallery Filters
                    updateGalleryFilters(currentOptions);
                });
            });

            // Initial Filter Run
            const initialOptions = product.variants[0].options;
            updateGalleryFilters(initialOptions);
        }

        // AJAX Cart Add
        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            addBtn.textContent = 'Adding...';

            const formData = new FormData(form);

            try {
                const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    body: formData
                });

                if (res.ok) {
                    await res.json(); // Consume the response to complete the request
                    addBtn.textContent = 'Added!';
                    setTimeout(() => {
                        if (!addBtn.disabled) addBtn.textContent = 'Add to Cart';
                    }, 2000);

                    // Update Cart Counter (Real-time)
                    fetch(window.Shopify.routes.root + '?section_id=cart-icon-bubble')
                        .then(res => res.text())
                        .then(html => {
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(html, 'text/html');
                            const newContent = doc.getElementById('shopify-section-cart-icon-bubble');
                            const cartIcon = document.getElementById('cart-icon-bubble');
                            if (cartIcon && newContent) {
                                // Preserve the <a> tag structure and classes
                                // Store original classes and attributes
                                const originalClasses = cartIcon.className;
                                const originalHref = cartIcon.href;
                                const originalId = cartIcon.id;
                                
                                // Update innerHTML while preserving the outer structure
                                cartIcon.innerHTML = newContent.innerHTML;
                                
                                // Ensure classes and attributes are preserved
                                cartIcon.className = originalClasses;
                                cartIcon.href = originalHref;
                                cartIcon.id = originalId;
                                
                                // Force style recalculation to prevent shrinking
                                cartIcon.offsetHeight; // Trigger reflow
                            }
                        })
                        .catch(e => console.error('Error updating cart icon:', e));

                    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                        bubbles: true,
                        detail: json
                    }));
                } else {
                    addBtn.textContent = 'Error';
                }
            } catch (err) {
                console.error(err);
                addBtn.textContent = 'Failed';
            }
        });

    }
});
